import React from "react"
import { ivaAmount } from "@/lib/tax"
import Header from "@/components/Header"
import Footer from "@/components/Footer"
import { PageHeader } from "@/components/ui/page-header"
import { Button } from "@/components/ui/button"
import { FormErrorMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ShoppingBag, ChevronLeft, CreditCard, Clock, Truck, Store, CircleCheck, Bell, AlertTriangle } from "lucide-react"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Controller } from "react-hook-form"
import { Check } from "lucide-react"
import { cn } from "@/lib/utils"
import { useCheckout } from "@/hooks/useCheckout"
import { AddressSelector } from "@/components/catalog/checkout/AddressSelector"
import { CheckoutSummary } from "@/components/catalog/checkout/CheckoutSummary"
import { PaymentArea } from "@/components/catalog/checkout/PaymentArea"
import { ReviewSidebar } from "@/components/catalog/checkout/ReviewSidebar"
import { AddressFields } from "@/components/profile/AddressFields"
import { WarehouseDisplay } from "@/components/warehouse/WarehouseDisplay"

const Checkout = () => {
  const {
    isSubmitting,
    checkoutId,
    selectedAddressId,
    items,
    totalPrice,
    savedAddresses,
    isLoadingAddresses,
    warehouses,
    warehouseInventoryAvailability,
    setPickupWarehouse,
    form,
    text,
    navigate,
    clearCart,
    handleAddressSelect,
    onSubmit,
  } = useCheckout()

  const [expandedWarehouseIds, setExpandedWarehouseIds] = React.useState<Set<number>>(new Set())

  const getWarehouseAvailability = (productId: number | undefined, warehouseId: number) => {
    if (productId === undefined) return undefined
    return warehouseInventoryAvailability.find(
      (availability) =>
        availability.productId === productId && availability.warehouseId === warehouseId
    )
  }

  const getAvailabilityMessage = (availableQuantity: number, requestedQuantity: number) => {
    if (availableQuantity >= requestedQuantity) {
      return text.pickupAvailableNow.replace("{count}", availableQuantity.toString())
    }

    if (availableQuantity > 0) {
      return text.pickupPartialLive.replace("{count}", availableQuantity.toString())
    }

    return text.pickupNotifyWhenAvailable
  }

  const renderAvailabilityBadge = (availableQuantity: number, requestedQuantity: number) => {
    if (availableQuantity >= requestedQuantity) {
      return (
        <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-sm border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-emerald-600">
          <CircleCheck className="h-3 w-3" />
          {text.pickupAvailableBadge}
        </span>
      )
    }

    return (
      <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-sm border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-amber-600">
        <Bell className="h-3 w-3" />
        {text.pickupNotifyBadge}
      </span>
    )
  }

  const toggleWarehouseAddress = (itemId: number) => {
    setExpandedWarehouseIds((prev) => {
      const next = new Set(prev)
      next.has(itemId) ? next.delete(itemId) : next.add(itemId)
      return next
    })
  }

  if (items.length === 0 && !isSubmitting && !checkoutId) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <main className="container mx-auto flex flex-1 flex-col items-center justify-center px-4 py-32">
          <ShoppingBag className="mb-8 h-20 w-20 text-muted-foreground opacity-10" />
          <h2 className="mb-8 font-display text-5xl uppercase tracking-widest opacity-20">
            {text.emptyCart}
          </h2>
          <Button onClick={() => navigate("/catalog")} className="btn-industrial px-12">
            {text.goToCatalog}
          </Button>
        </main>
        <Footer />
      </div>
    )
  }

  const iva = items.reduce((sum, item) => {
    return sum + (item.isTaxable ? ivaAmount((item.price || 0) * item.quantity) : 0)
  }, 0)
  const total = totalPrice + iva

  const renderShippingLayout = () => (
    <>
      {warehouses.length > 0 && (
        <section className="space-y-8 transition-opacity">
          <div className="flex items-center gap-4">
            <div className="flex h-8 w-12 items-center justify-center border-l-[1px] border-accent bg-foreground/[0.03] font-mono text-xs font-black tracking-widest text-accent shadow-sm">
              [01]
            </div>
            <div className="space-y-2">
              <h2 className="font-display text-3xl uppercase tracking-widest">{text.fulfillment}</h2>
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                {text.fulfillmentSubtitle}
              </p>
              <div className="h-[1px] w-8 bg-accent" />
            </div>
          </div>

          <div className="overflow-hidden rounded-lg bg-foreground/10 p-px shadow-2xl">
            <div className="divide-y divide-foreground/5 bg-background">
              {items.map((item) => {
                const selectedWarehouse = item.pickupWarehouseId
                  ? warehouses.find((w) => w.id === item.pickupWarehouseId)
                  : undefined
                const isExpanded = item.id !== undefined && expandedWarehouseIds.has(item.id)
                const selectedAvailability = selectedWarehouse
                  ? getWarehouseAvailability(item.id, selectedWarehouse.id)
                  : undefined
                const selectedAvailableQuantity = selectedAvailability?.availableQuantity ?? 0

                return (
                  <div key={item.id} className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <p className="font-display text-xl uppercase tracking-wider text-foreground">
                          {item.name}
                        </p>
                        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                          {item.quantity}× {item.weight} {item.weightUnit}
                        </p>
                      </div>
                      <Select
                        value={item.pickupWarehouseId ? String(item.pickupWarehouseId) : "delivery"}
                        onValueChange={(val) => {
                          if (item.id === undefined) return
                          setPickupWarehouse(item.id, val === "delivery" ? undefined : Number(val))
                          setExpandedWarehouseIds((prev) => {
                            const next = new Set(prev)
                            next.delete(item.id!)
                            return next
                          })
                        }}
                      >
                        <SelectTrigger className="w-52 rounded-lg border-border text-xs">
                          <span className="flex items-center gap-2">
                            <SelectValue />
                          </span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="delivery">
                            <span className="flex items-center gap-2 text-xs">
                              <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                              {text.delivery}
                            </span>
                          </SelectItem>
                          {warehouses.map((w) => (
                            <SelectItem key={w.id} value={String(w.id)}>
                              <span className="flex w-full min-w-72 items-center gap-2 text-xs">
                                <Store className="h-3.5 w-3.5 text-cyan-500" />
                                <span className="truncate">{w.name}</span>
                                {renderAvailabilityBadge(
                                  getWarehouseAvailability(item.id, w.id)?.availableQuantity ?? 0,
                                  item.quantity
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedWarehouse && (
                      <div
                        className={cn(
                          "mt-4 flex items-start gap-3 rounded-md border px-4 py-3 text-xs font-bold leading-relaxed",
                          selectedAvailableQuantity >= item.quantity
                            ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-700"
                            : "border-amber-500/20 bg-amber-500/10 text-amber-700"
                        )}
                      >
                        {selectedAvailableQuantity >= item.quantity ? (
                          <CircleCheck className="mt-0.5 h-4 w-4 shrink-0" />
                        ) : (
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        )}
                        <span>
                          {getAvailabilityMessage(selectedAvailableQuantity, item.quantity)}
                        </span>
                      </div>
                    )}

                    {selectedWarehouse && (
                      <WarehouseDisplay
                        warehouse={selectedWarehouse}
                        isExpanded={isExpanded}
                        onToggle={() => item.id !== undefined && toggleWarehouseAddress(item.id)}
                        className="mt-3 ml-0"
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </section>
      )}

      <section className="space-y-8 transition-opacity">
        <div className="flex items-center gap-4">
          <div className="flex h-8 w-12 items-center justify-center border-l-[1px] border-accent bg-foreground/[0.03] font-mono text-xs font-black tracking-widest text-accent shadow-sm">
            {warehouses.length > 0 ? "[02]" : "[01]"}
          </div>
          <div className="space-y-2">
            <h2 className="font-display text-3xl uppercase tracking-widest">{text.shipping}</h2>
            <div className="h-[1px] w-8 bg-accent" />
          </div>
        </div>

        <div className="overflow-hidden rounded-lg bg-foreground/10 p-px shadow-2xl">
          <div className="space-y-8 bg-background p-8 md:p-12">
            <AddressSelector
              savedAddresses={savedAddresses}
              selectedAddressId={selectedAddressId}
              isLoading={isLoadingAddresses}
              onSelect={handleAddressSelect}
              text={text}
            />

            <form id="checkout-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <fieldset disabled={isSubmitting} className="space-y-8">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="receiverName">{text.receiver}</Label>
                    <Input id="receiverName" {...form.register("receiverName")} />
                    <FormErrorMessage>
                      {form.formState.errors.receiverName?.message as string}
                    </FormErrorMessage>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{text.phone}</Label>
                    <Input id="phone" {...form.register("phone")} />
                    <FormErrorMessage>
                      {form.formState.errors.phone?.message as string}
                    </FormErrorMessage>
                  </div>
                </div>
                <AddressFields
                  register={form.register}
                  errors={form.formState.errors}
                  control={form.control}
                  setValue={form.setValue}
                  prefix="address"
                />
                {selectedAddressId === "new" && (
                  <div className="flex items-center gap-3 border-t border-foreground/5 pt-4">
                    <input
                      type="checkbox"
                      id="saveAddress"
                      {...form.register("saveAddress")}
                      className="h-5 w-5 rounded-sm border-input bg-background text-accent focus:ring-accent disabled:opacity-50"
                    />
                    <Label
                      htmlFor="saveAddress"
                      className="cursor-pointer text-xs font-bold uppercase tracking-widest text-muted-foreground"
                    >
                      {text.saveForFuture}
                    </Label>
                  </div>
                )}
              </fieldset>
            </form>
          </div>
        </div>
      </section>

      <section className="space-y-8 transition-opacity">
        <div className="flex items-center gap-4">
          <div className="flex h-8 w-12 items-center justify-center border-l-[1px] border-accent bg-foreground/[0.03] font-mono text-xs font-black tracking-widest text-accent shadow-sm">
            {warehouses.length > 0 ? "[03]" : "[02]"}
          </div>
          <div className="space-y-2">
            <h2 className="font-display text-3xl uppercase tracking-widest">
              {text.paymentMethod}
            </h2>
            <div className="h-[1px] w-8 bg-accent" />
          </div>
        </div>

        <div className="space-y-4">
          <Controller
            name="paymentMethod"
            control={form.control}
            render={({ field }) => (
              <RadioGroup
                onValueChange={field.onChange}
                value={field.value}
                className="grid grid-cols-1 gap-4"
              >
                {/* For simplicity, we're only implementing "Pay Later" in this example. You can easily add more options like "Pay Now" by following the same pattern.*/}
                {/* <div className="relative">
                  <RadioGroupItem
                    value="now"
                    id="pay-now"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="pay-now"
                    className={cn(
                      "group relative flex cursor-pointer flex-col items-start justify-start rounded-lg border-[1px] p-6 transition-all duration-300",
                      field.value === "now"
                        ? "border-accent bg-accent/5 shadow-lg"
                        : "border-border bg-muted/50 hover:border-foreground/20"
                    )}
                  >
                    {field.value === "now" && (
                      <div className="absolute right-0 top-0 flex h-6 w-6 items-center justify-center bg-accent">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                    <div className="flex items-center gap-4">
                      <CreditCard className="h-5 w-5 text-accent" />
                      <p className="font-display text-xl tracking-wider text-foreground">
                        {text.payNow}
                      </p>
                    </div>
                    <p className="mt-2 text-xs font-bold text-muted-foreground opacity-60">
                      {text.paymentDesc}
                    </p>
                  </Label>
                </div> */}

                <div className="relative">
                  <RadioGroupItem
                    value="later"
                    id="pay-later"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="pay-later"
                    className={cn(
                      "group relative flex cursor-pointer flex-col items-start justify-start rounded-lg border-[1px] p-6 transition-all duration-300",
                      field.value === "later"
                        ? "border-accent bg-accent/5 shadow-lg"
                        : "border-border bg-muted/50 hover:border-foreground/20"
                    )}
                  >
                    {field.value === "later" && (
                      <div className="absolute right-0 top-0 flex h-6 w-6 items-center justify-center bg-accent">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                    <div className="flex items-center gap-4">
                      <Clock className="h-5 w-5 text-accent" />
                      <p className="font-display text-xl tracking-wider text-foreground">
                        {text.payLater}
                      </p>
                    </div>
                    <p className="mt-2 text-xs font-bold leading-tight text-muted-foreground opacity-60">
                      {text.payLaterDesc}
                    </p>
                  </Label>
                </div>
              </RadioGroup>
            )}
          />
        </div>
      </section>
    </>
  )

  const renderPaymentLayout = () => (
    <PaymentArea
      checkoutId={checkoutId!}
      text={text}
      clearCart={clearCart}
    />
  )

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />

      <main className="container mx-auto flex-1 px-4 py-12">
        <PageHeader
          title={text.title}
          subtitle={text.subtitle}
          subtitleClassName="tracking-subtitle font-bold"
        >
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="group flex h-auto items-center gap-2 p-0 font-display tracking-widest text-muted-foreground transition-colors hover:bg-transparent hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            {text.back}
          </Button>
        </PageHeader>

        {/* Global Visual Stepper */}
        <div className="mb-12 flex items-center justify-center px-4">
          <div className="flex w-full max-w-3xl items-center">
            <div className="flex flex-col items-center gap-2 relative z-10 bg-background px-4">
              <div className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${!checkoutId ? 'border-accent bg-accent text-primary-foreground' : 'border-accent bg-background text-accent'}`}>
                <span className="font-display text-xl">1</span>
              </div>
              <span className={`text-xs font-bold uppercase tracking-wider ${!checkoutId ? 'text-accent' : 'text-muted-foreground'}`}>{text.shipping}</span>
            </div>
            <div className={`-mx-6 h-[2px] flex-1 ${checkoutId ? 'bg-accent' : 'bg-border'}`} />
            <div className="flex flex-col items-center gap-2 relative z-10 bg-background px-4">
              <div className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${checkoutId ? 'border-accent bg-accent text-primary-foreground' : 'border-border bg-background text-muted-foreground'}`}>
                <span className="font-display text-xl">2</span>
              </div>
              <span className={`text-xs font-bold tracking-wider ${checkoutId ? 'text-accent' : 'text-muted-foreground'}`}>{text.paymentMethod}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-12">
          <div className="space-y-12 lg:col-span-7">
            {checkoutId ? renderPaymentLayout() : renderShippingLayout()}
          </div>

          <aside className="space-y-8 lg:sticky lg:top-32 lg:col-span-5">
            {checkoutId ? (
              <ReviewSidebar
                items={items}
                total={total}
                text={text}
                navigate={navigate}
                getValues={form.getValues}
              />
            ) : (
              <>
                <CheckoutSummary
                  items={items}
                  totalPrice={totalPrice}
                  isSubmitting={isSubmitting}
                  onSubmit={form.handleSubmit(onSubmit)}
                  text={text}
                />

                <div className="relative rounded-r-lg border-l-[1px] border-accent bg-muted/30 p-6 shadow-sm">
                  <div className="absolute right-0 top-0 h-1 w-1 bg-accent/20" />
                  <p className="mb-2 text-xs font-black uppercase tracking-[0.2em] text-accent">
                    {text.support}
                  </p>
                  <p className="text-xs font-medium leading-relaxed text-muted-foreground">
                    {text.supportDesc}
                  </p>
                </div>
              </>
            )}
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default Checkout

